import { createSlice } from '@reduxjs/toolkit';
import {
  fetchAdminHealthThunk,
  fetchAdminUserThunk,
  bulkAdminUserActionThunk,
  eraseAdminUserThunk,
  exportAdminDataThunk,
  fetchAdminSystemStatsThunk,
  fetchAdminUsersListThunk,
  fetchImportModelsThunk,
  validateCSVThunk,
  importCSVThunk,
  fetchAdminAuditLogsThunk
} from './adminActionThunks';

const initialState = {
  // Health and system data
  health: null,
  systemStats: null,
  
  // User management
  userDetails: null,
  users: [],
  usersMetadata: {
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  },
  
  // Audit logs
  auditLogs: [],
  auditLogsMetadata: {
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  },
  
  // Actions and operations
  bulkActionResult: null,
  eraseResult: null,
  exportedData: null,
  
  // CSV Import functionality
  importModels: [],
  csvValidation: null,
  csvImportResult: null,

  // CSV Wizard UI state (persist across re-renders)
  csvWizard: {
    step: 1,
    selectedModel: null,
    csvFile: null,
    fieldMapping: {}
  },
  
  // Loading states
  loading: {
    health: false,
    userDetails: false,
    users: false,
    bulkAction: false,
    eraseUser: false,
    exportData: false,
    systemStats: false,
    auditLogs: false,
    importModels: false,
    csvValidation: false,
    csvImport: false,
  },
  
  // Error states
  errors: {
    health: null,
    userDetails: null,
    users: null,
    bulkAction: null,
    eraseUser: null,
    exportData: null,
    systemStats: null,
    auditLogs: null,
    importModels: null,
    csvValidation: null,
    csvImport: null,
  },
  
  // General error (for backward compatibility)
  error: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    resetAdminError: (state) => { 
      state.error = null;
      // Reset all specific errors
      Object.keys(state.errors).forEach(key => {
        state.errors[key] = null;
      });
    },
    resetAdminState: () => initialState,
    resetBulkActionResult: (state) => {
      state.bulkActionResult = null;
    },
    resetExportData: (state) => {
      state.exportedData = null;
    },
    clearUserDetails: (state) => {
      state.userDetails = null;
    },
    updateUserInList: (state, action) => {
      const { userId, updates } = action.payload;
      const userIndex = state.users.findIndex(user => user.id === userId);
      if (userIndex !== -1) {
        state.users[userIndex] = { ...state.users[userIndex], ...updates };
      }
    },

    // === CSV Wizard reducers ===
    setCsvWizardStep: (state, action) => {
      state.csvWizard.step = action.payload;
    },
    setCsvWizardModel: (state, action) => {
      state.csvWizard.selectedModel = action.payload;
    },
    setCsvWizardFile: (state, action) => {
      state.csvWizard.csvFile = action.payload
        ? { name: action.payload.name, size: action.payload.size, type: action.payload.type }
        : null;
    },
    setCsvWizardFieldMapping: (state, action) => {
      state.csvWizard.fieldMapping = action.payload;
    },
    resetCsvWizard: (state) => {
      state.csvWizard = initialState.csvWizard;
      state.csvValidation = null;
      state.csvImportResult = null;
    }
  },
  extraReducers: (builder) => {
    // === Health Status ===
    builder
      .addCase(fetchAdminHealthThunk.pending, (state) => {
        state.loading.health = true;
        state.errors.health = null;
      })
      .addCase(fetchAdminHealthThunk.fulfilled, (state, action) => {
        state.health = action.payload;
        state.loading.health = false;
        state.errors.health = null;
      })
      .addCase(fetchAdminHealthThunk.rejected, (state, action) => {
        state.loading.health = false;
        state.errors.health = action.payload || 'Failed to fetch health status';
        state.error = action.payload;
      })

    // === Individual User Details ===
      .addCase(fetchAdminUserThunk.pending, (state) => {
        state.loading.userDetails = true;
        state.errors.userDetails = null;
      })
      .addCase(fetchAdminUserThunk.fulfilled, (state, action) => {
        state.userDetails = action.payload;
        state.loading.userDetails = false;
        state.errors.userDetails = null;
      })
      .addCase(fetchAdminUserThunk.rejected, (state, action) => {
        state.loading.userDetails = false;
        state.errors.userDetails = action.payload || 'Failed to fetch user details';
        state.error = action.payload;
      })

    // === Users List ===
      .addCase(fetchAdminUsersListThunk.pending, (state) => {
        state.loading.users = true;
        state.errors.users = null;
      })
      .addCase(fetchAdminUsersListThunk.fulfilled, (state, action) => {
        const { users, metadata } = action.payload;
        state.users = users || action.payload;
        state.usersMetadata = metadata || state.usersMetadata;
        state.loading.users = false;
        state.errors.users = null;
      })
      .addCase(fetchAdminUsersListThunk.rejected, (state, action) => {
        state.loading.users = false;
        state.errors.users = action.payload || 'Failed to fetch users list';
        state.error = action.payload;
      })

    // === Bulk User Actions ===
      .addCase(bulkAdminUserActionThunk.pending, (state) => {
        state.loading.bulkAction = true;
        state.errors.bulkAction = null;
      })
      .addCase(bulkAdminUserActionThunk.fulfilled, (state, action) => {
        state.bulkActionResult = action.payload;
        state.loading.bulkAction = false;
        state.errors.bulkAction = null;
      })
      .addCase(bulkAdminUserActionThunk.rejected, (state, action) => {
        state.loading.bulkAction = false;
        state.errors.bulkAction = action.payload || 'Bulk action failed';
        state.error = action.payload;
      })

    // === Erase User Account (GDPR Article 17) ===
      .addCase(eraseAdminUserThunk.pending, (state) => {
        state.loading.eraseUser = true;
        state.errors.eraseUser = null;
      })
      .addCase(eraseAdminUserThunk.fulfilled, (state, action) => {
        state.eraseResult = action.payload;
        state.loading.eraseUser = false;
        state.errors.eraseUser = null;
      })
      .addCase(eraseAdminUserThunk.rejected, (state, action) => {
        state.loading.eraseUser = false;
        state.errors.eraseUser = action.payload || 'Account erasure failed';
        state.error = action.payload;
      })

    // === Data Export ===
      .addCase(exportAdminDataThunk.pending, (state) => {
        state.loading.exportData = true;
        state.errors.exportData = null;
      })
      .addCase(exportAdminDataThunk.fulfilled, (state, action) => {
        state.exportedData = action.payload;
        state.loading.exportData = false;
        state.errors.exportData = null;
      })
      .addCase(exportAdminDataThunk.rejected, (state, action) => {
        state.loading.exportData = false;
        state.errors.exportData = action.payload || 'Data export failed';
        state.error = action.payload;
      })

    // === System Stats ===
      .addCase(fetchAdminSystemStatsThunk.pending, (state) => {
        state.loading.systemStats = true;
        state.errors.systemStats = null;
      })
      .addCase(fetchAdminSystemStatsThunk.fulfilled, (state, action) => {
        state.systemStats = action.payload;
        state.loading.systemStats = false;
        state.errors.systemStats = null;
      })
      .addCase(fetchAdminSystemStatsThunk.rejected, (state, action) => {
        state.loading.systemStats = false;
        state.errors.systemStats = action.payload || 'Failed to fetch system stats';
        state.error = action.payload;
      })

    // === Audit Logs ===
      .addCase(fetchAdminAuditLogsThunk.pending, (state) => {
        state.loading.auditLogs = true;
        state.errors.auditLogs = null;
      })
      .addCase(fetchAdminAuditLogsThunk.fulfilled, (state, action) => {
        const { logs, metadata } = action.payload;
        state.auditLogs = logs || action.payload;
        state.auditLogsMetadata = metadata || state.auditLogsMetadata;
        state.loading.auditLogs = false;
        state.errors.auditLogs = null;
      })
      .addCase(fetchAdminAuditLogsThunk.rejected, (state, action) => {
        state.loading.auditLogs = false;
        state.errors.auditLogs = action.payload || 'Failed to fetch audit logs';
        state.error = action.payload;
      })

    // === Import Models ===
      .addCase(fetchImportModelsThunk.pending, (state) => {
        state.loading.importModels = true;
        state.errors.importModels = null;
      })
      .addCase(fetchImportModelsThunk.fulfilled, (state, action) => {
        state.loading.importModels = false;
        state.importModels = action.payload;
        state.errors.importModels = null;
      })
      .addCase(fetchImportModelsThunk.rejected, (state, action) => {
        state.loading.importModels = false;
        state.errors.importModels = action.payload || 'Failed to fetch import models';
      })

    // === CSV Validation ===
      .addCase(validateCSVThunk.pending, (state) => {
        state.loading.csvValidation = true;
        state.errors.csvValidation = null;
      })
      .addCase(validateCSVThunk.fulfilled, (state, action) => {
        state.loading.csvValidation = false;
        state.csvValidation = action.payload;
        state.errors.csvValidation = null;
        // Advance wizard automatically if valid
        state.csvWizard.step = 4;
      })
      .addCase(validateCSVThunk.rejected, (state, action) => {
        state.loading.csvValidation = false;
        state.errors.csvValidation = action.payload || 'CSV validation failed';
      })

    // === CSV Import ===
      .addCase(importCSVThunk.pending, (state) => {
        state.loading.csvImport = true;
        state.errors.csvImport = null;
      })
      .addCase(importCSVThunk.fulfilled, (state, action) => {
        state.loading.csvImport = false;
        state.csvImportResult = action.payload;
        state.errors.csvImport = null;
        // Move wizard to import done
        state.csvWizard.step = 5;
      })
      .addCase(importCSVThunk.rejected, (state, action) => {
        state.loading.csvImport = false;
        state.errors.csvImport = action.payload || 'CSV import failed';
      });
  },
});

export const { 
  resetAdminError, 
  resetAdminState, 
  resetBulkActionResult, 
  resetExportData, 
  clearUserDetails,
  updateUserInList,
  setCsvWizardStep,
  setCsvWizardModel,
  setCsvWizardFile,
  setCsvWizardFieldMapping,
  resetCsvWizard
} = adminSlice.actions;

export default adminSlice.reducer;
